import { useSubscription, useSuspenseQuery } from "@apollo/client/react";
import { Address, KeybanNft, SdkError, SdkErrorTypes } from "@keyban/sdk-base";
import { nftDocument, nftSubscriptionDocument } from "@keyban/sdk-base/graphql";

import { useKeybanClient } from "~/provider";
import { ApiResult } from "~/types";

/**
 * Retrives a single nft data.
 * @param tokenAddress - The address of the token contract.
 * @param tokenId - The ID of the token.
 * @returns - The result containing the NFT or an error.
 */
export function useKeybanNft(
  tokenAddress: Address,
  tokenId: string,
): ApiResult<KeybanNft> {
  const client = useKeybanClient();

  const id = [tokenAddress, tokenId].join(":");

  const { data, error } = useSuspenseQuery(nftDocument, {
    client: client.apolloClient,
    variables: { nftId: id },
  });

  useSubscription(nftSubscriptionDocument, {
    client: client.apolloClient,
    variables: {
      nftIds: [id],
    },
    onData({ client, data: { data } }) {
      // Setting up the subscription isn't enouth, in case of an insert, the
      // cache won't be updated.
      client.cache.updateQuery(
        {
          query: nftDocument,
          variables: { nftId: id },
        },
        () => ({ res: data!.sub!.entity }),
      );
    },
  });

  if (error) return [null, error, undefined] as const;
  if (!data.res)
    return [
      null,
      new SdkError(SdkErrorTypes.NftNotFound, "useKeybanNft"),
      undefined,
    ];

  return [data.res, null, undefined] as const;
}
